// LibraryPanel — catalogue, issue a book to a student or staff member,
// take it back (with optional fine), and see what's overdue.
import { useEffect, useState } from 'react';
import { Plus, Trash2, BookCheck, Search } from 'lucide-react';
import { getBooks, addBook, deleteBook, getBookIssues, issueBook, returnBook, getStaff } from '../api/client';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { Card, CardHeader } from '../ui/Card';
import { Field, Msg, Modal, errText, inputCls, theadRowCls, EmptyRow } from '../ui/Form';
import StudentPicker from './StudentPicker';
import { inr, fmtDate } from '../ui/format';

export default function LibraryPanel() {
  const [tab,    setTab]    = useState('issues');
  const [books,  setBooks]  = useState([]);
  const [q,      setQ]      = useState('');
  const [issues, setIssues] = useState([]);
  const [status, setStatus] = useState('open');
  const [staff,  setStaff]  = useState([]);
  const [bookForm, setBookForm] = useState(null);
  const [issueForm, setIssueForm] = useState(null);
  const [msg,    setMsg]    = useState('');

  useEffect(() => { getStaff().then(setStaff).catch(() => {}); }, []);
  useEffect(() => { loadBooks(); }, [q]);
  useEffect(() => { loadIssues(); }, [status]);

  async function loadBooks() { try { setBooks(await getBooks(q.trim())); } catch (err) { setMsg(errText(err)); } }
  async function loadIssues() { try { setIssues(await getBookIssues({ status })); } catch (err) { setMsg(errText(err)); } }
  const reload = () => { loadBooks(); loadIssues(); };

  async function saveBook(e) {
    e.preventDefault(); setMsg('');
    try { await addBook({ ...bookForm, copies: Number(bookForm.copies) || 1 }); setBookForm(null); setMsg('Book added ✅'); reload(); }
    catch (err) { setMsg(errText(err)); }
  }
  async function removeBook(b) {
    if (!window.confirm(`Remove "${b.title}" from the catalogue?`)) return;
    try { await deleteBook(b.id); reload(); } catch (err) { setMsg(errText(err)); }
  }
  async function saveIssue(e) {
    e.preventDefault(); setMsg('');
    if (issueForm.to === 'student' && !issueForm.student) return setMsg('Error: pick a student');
    try {
      await issueBook({
        book_id: Number(issueForm.book_id),
        student_id: issueForm.to === 'student' ? issueForm.student.id : undefined,
        staff_id: issueForm.to === 'staff' ? Number(issueForm.staff_id) : undefined,
        due_on: issueForm.due_on || undefined,
      });
      setIssueForm(null); setMsg('Book issued ✅'); setStatus('open'); reload();
    } catch (err) { setMsg(errText(err)); }
  }
  async function takeBack(i) {
    const suggested = i.days_overdue > 0 ? i.days_overdue * 1 : 0;
    const input = window.prompt(`Return "${i.title}" from ${i.borrower_name}.\nFine (₹) — ${i.days_overdue} day(s) late:`, String(suggested));
    if (input === null) return;
    try { await returnBook(i.id, Number(input) || 0); setMsg('Book returned ✅'); reload(); } catch (err) { setMsg(errText(err)); }
  }

  const available = books.filter(b => b.available > 0);
  const overdueCount = issues.filter(i => i.overdue).length;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h2 className="font-display font-extrabold text-xl text-ink">Library</h2>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setBookForm({ title: '', author: '', isbn: '', category: '', copies: 1 })} className="flex items-center gap-1.5"><Plus size={14} /> Add book</Button>
          <Button onClick={() => setIssueForm({ book_id: '', to: 'student', student: null, staff_id: '', due_on: '' })} className="flex items-center gap-1.5"><BookCheck size={14} /> Issue book</Button>
        </div>
      </div>
      <Msg msg={msg} className="mb-3" />

      <div className="flex gap-1.5 mb-4 bg-transit-tint p-1 rounded-m w-fit">
        {[['issues', 'Issued books'], ['books', `Catalogue (${books.length})`]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 rounded-s text-[13px] font-bold ${tab === k ? 'bg-transit text-white shadow-1' : 'text-transit-deep'}`}>{label}</button>
        ))}
      </div>

      {tab === 'issues' && (
        <Card>
          <CardHeader title={`${issues.length} ${status === 'returned' ? 'returned' : status === 'overdue' ? 'overdue' : 'issued'}${status === 'open' && overdueCount ? ` · ${overdueCount} overdue` : ''}`} action={
            <select className={inputCls} value={status} onChange={e => setStatus(e.target.value)} aria-label="Show">
              <option value="open">Currently issued</option><option value="overdue">Overdue</option><option value="returned">Returned</option><option value="all">All</option>
            </select>
          } />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className={theadRowCls}><th className="px-4 py-2.5">Book</th><th className="px-4 py-2.5">Borrower</th><th className="px-4 py-2.5">Issued</th><th className="px-4 py-2.5">Due</th><th className="px-4 py-2.5"></th></tr></thead>
              <tbody>
                {issues.map(i => (
                  <tr key={i.id} className="border-t border-line-soft">
                    <td className="px-4 py-3"><b className="text-ink">{i.title}</b>{i.author && <span className="block text-xs text-ink-soft">{i.author}</span>}</td>
                    <td className="px-4 py-3">{i.borrower_name}<span className="block text-xs text-ink-soft">{i.borrower_class === 'Staff' ? 'Staff' : `Class ${i.borrower_class || '—'}`}</span></td>
                    <td className="px-4 py-3 whitespace-nowrap">{fmtDate(i.issued_on)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{fmtDate(i.due_on)}{i.overdue && <span className="block"><Badge tone="critical">{i.days_overdue}d late</Badge></span>}</td>
                    <td className="px-4 py-3 text-right">
                      {i.returned_on
                        ? <span className="text-xs text-ink-soft">Returned {fmtDate(i.returned_on)}{i.fine > 0 ? ` · fine ${inr(i.fine)}` : ''}</span>
                        : <Button variant="secondary" className="!px-2.5 !py-1 !text-xs" onClick={() => takeBack(i)}>Return</Button>}
                    </td>
                  </tr>
                ))}
                {!issues.length && <EmptyRow cols={5}>Nothing here</EmptyRow>}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === 'books' && (
        <Card>
          <CardHeader title="Catalogue" action={
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-soft" />
              <input className={`${inputCls} pl-8`} placeholder="Title, author, ISBN" value={q} onChange={e => setQ(e.target.value)} aria-label="Search books" />
            </div>
          } />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className={theadRowCls}><th className="px-4 py-2.5">Title</th><th className="px-4 py-2.5">Category</th><th className="px-4 py-2.5">ISBN</th><th className="px-4 py-2.5">Copies</th><th className="px-4 py-2.5">Available</th><th className="px-4 py-2.5"></th></tr></thead>
              <tbody>
                {books.map(b => (
                  <tr key={b.id} className="border-t border-line-soft">
                    <td className="px-4 py-3"><b className="text-ink">{b.title}</b>{b.author && <span className="block text-xs text-ink-soft">{b.author}</span>}</td>
                    <td className="px-4 py-3">{b.category || '—'}</td>
                    <td className="px-4 py-3 text-ink-soft">{b.isbn || '—'}</td>
                    <td className="px-4 py-3 tabular-nums">{b.copies}</td>
                    <td className={`px-4 py-3 tabular-nums font-bold ${b.available ? 'text-success' : 'text-critical'}`}>{b.available}</td>
                    <td className="px-4 py-3"><button onClick={() => removeBook(b)} aria-label="Remove book" className="text-ink-soft hover:text-critical p-1"><Trash2 size={14} /></button></td>
                  </tr>
                ))}
                {!books.length && <EmptyRow cols={6}>No books yet — click Add book</EmptyRow>}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {bookForm && (
        <Modal title="Add book" onClose={() => setBookForm(null)} width={440}>
          <form onSubmit={saveBook} className="flex flex-col gap-3">
            <Field label="Title"><input required className={inputCls} value={bookForm.title} onChange={e => setBookForm({ ...bookForm, title: e.target.value })} /></Field>
            <Field label="Author"><input className={inputCls} value={bookForm.author} onChange={e => setBookForm({ ...bookForm, author: e.target.value })} /></Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Category" className="col-span-2"><input className={inputCls} placeholder="e.g. Fiction" value={bookForm.category} onChange={e => setBookForm({ ...bookForm, category: e.target.value })} /></Field>
              <Field label="Copies"><input type="number" min="1" className={inputCls} value={bookForm.copies} onChange={e => setBookForm({ ...bookForm, copies: e.target.value })} /></Field>
            </div>
            <Field label="ISBN"><input className={inputCls} value={bookForm.isbn} onChange={e => setBookForm({ ...bookForm, isbn: e.target.value })} /></Field>
            <Button type="submit" className="w-fit">Add book</Button>
          </form>
        </Modal>
      )}

      {issueForm && (
        <Modal title="Issue book" onClose={() => setIssueForm(null)} width={460}>
          <form onSubmit={saveIssue} className="flex flex-col gap-3">
            <Field label="Book">
              <select required className={inputCls} value={issueForm.book_id} onChange={e => setIssueForm({ ...issueForm, book_id: e.target.value })}>
                <option value="" disabled>Pick a book ({available.length} available)</option>
                {available.map(b => <option key={b.id} value={b.id}>{b.title}{b.author ? ` — ${b.author}` : ''} ({b.available} left)</option>)}
              </select>
            </Field>
            <div className="flex gap-1.5">
              {['student', 'staff'].map(k => (
                <button type="button" key={k} onClick={() => setIssueForm({ ...issueForm, to: k })}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border ${issueForm.to === k ? 'bg-transit text-white border-transit' : 'border-line text-ink'}`}>
                  To {k}
                </button>
              ))}
            </div>
            {issueForm.to === 'student'
              ? <StudentPicker value={issueForm.student} onChange={s => setIssueForm({ ...issueForm, student: s })} />
              : <select required className={inputCls} value={issueForm.staff_id} onChange={e => setIssueForm({ ...issueForm, staff_id: e.target.value })}>
                  <option value="" disabled>Pick staff member</option>
                  {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>}
            <Field label="Return by (default 14 days)"><input type="date" className={inputCls} value={issueForm.due_on} onChange={e => setIssueForm({ ...issueForm, due_on: e.target.value })} /></Field>
            <Button type="submit" className="w-fit">Issue</Button>
          </form>
        </Modal>
      )}
    </div>
  );
}
